import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ExpansionGroup from '../../components/expansion-group/expansion-group.component';
import List from '../../components/list/list.component';
import ListItem from '../../components/list-item/list-item.component';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import CheckAccess from '../../components/check-access/check-access.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import ContextMenu from '../../components/context-menu/context-menu.component';
import FilteredLink from '../../components/filtered-link/filtered-link.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import ActionButton from '../../components/action-button/action-button.component';
import Controls from '../app/controls/controls.component';
import DeleteItemIcon from '../../icons/delete-item-icon';
import MoreIcon from '../../icons/more-icon';
import ScreenService from '../../services/screen-service';
import useScreenSmall from '../../hooks/use-screen-small';
import { getReportListData, deleteReport } from './report-list.action';
import { REPORT_LIST_VIEW, REPORT_DELETE, REPORT_VIEW } from '../../constants/entity/permissions';
import { DATA_REPORT_ID } from '../../constants/system/data-attributes';
import { ROUTE_REPORT } from '../../constants/system/routes';

const ReportList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDataLoaded,
    reportListData,
  } = props;

  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [reportActiveId, setReportActiveId]: [number | null, Function] = useState(null);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const navigate: ReactRouterNavigate = useNavigate();

  const pageTitle: string = 'Reports';
  const deleteConfirmModalTitle: string = 'Delete Report';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this report?';

  const reportListViewPermissions: string[] = useMemo((): string[] => [REPORT_LIST_VIEW], []);

  const openDeleteConfirmModal: Function = useCallback((reportId: number): void => {
    setReportActiveId(reportId);
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: REPORT_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, reportId: number): void => {
        if (reportId) {
          openDeleteConfirmModal(reportId);
        }
      },
    },
  ], []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!reportActiveId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteReport({
        reportId: reportActiveId,
        isListItem: true,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDeleteConfirmModalOpen(false);

        setReportActiveId(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [reportActiveId]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setConfirmModalErrorMessage(null);
    setIsDeleteConfirmModalOpen(false);
  }, []);

  const onRowClick: Function = useCallback((event: React.MouseEvent<Element, MouseEvent>, reportId: number): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      navigate({
        pathname: `${ROUTE_REPORT}/${reportId}`,
      });
    }
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getReportListData());
  }, []);

  useEffect((): void => {
    dispatch(getReportListData());
  }, []);

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={reportListViewPermissions}>
      <PullToRefresh onRefresh={onRefresh}>
        <ContentHeader title={pageTitle} />
        <Controls isHidden />
        <Fragment>
          {
            reportListData && !!reportListData.length
            && reportListData.map((reportGroup: ReportGroup): ReactElement => (
              <ExpansionGroup
                isDefaultExpanded={reportGroup.reports && !!reportGroup.reports.length}
                key={reportGroup.id}
                headerComponent={reportGroup.title}
              >
                <List>
                  {
                    reportGroup.reports.map((report: Report): ReactElement => (
                      <ListItem
                        isButton
                        isExtended
                        withContentSpread
                        key={report.id}
                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          onRowClick(event, report.id);
                        }}
                      >
                        <FilteredLink link={`${ROUTE_REPORT}/${report.id}`} permission={REPORT_VIEW}>
                          {report.name}
                        </FilteredLink>
                        {
                          !report.isSystem && !isSmallScreen && (
                            <div>
                              <CheckAccess
                                permissions={actionMenuItems
                                  && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => (
                                    actionMenuItem.permission
                                  ))}
                              >
                                <ActionButton
                                  withPopup
                                  withNoPadding
                                  onClick={openActionMenu}
                                  {...{
                                    [DATA_REPORT_ID]: report.id,
                                  }}
                                >
                                  <MoreIcon />
                                </ActionButton>
                              </CheckAccess>
                            </div>
                          )
                        }
                      </ListItem>
                    ))
                  }
                </List>
              </ExpansionGroup>
            ))
          }
        </Fragment>
      </PullToRefresh>
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_REPORT_ID}
        onClose={closeActionMenu}
      />
      <ConfirmModal
        isOpen={isDeleteConfirmModalOpen}
        title={deleteConfirmModalTitle}
        text={deleteConfirmModalText}
        errorMessage={confirmModalErrorMessage}
        onConfirm={onDeleteConfirm}
        onCloseModal={closeDeleteConfirmModal}
      />
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </CheckAccess>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    reportList,
  } = reducers;

  return {
    isDataLoaded: !!reportList.reportListData,
    reportListData: reportList.reportListData && reportList.reportListData.reportGroupList,
  };
};

export default connect(mapState)(ReportList);
